import React, { useEffect, useState } from "react";
import {
    Button,
    Checkbox,
    Grid,
    Group,
    Modal,
    Select,
    Stack
} from "@mantine/core";
import { useForm } from "@mantine/form";
import AugmentedFoodService from "../../../services/apps/AugmentedFoodService";
import { genericError } from "../../../functions/genericError";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/pro-regular-svg-icons/faSpinner";
import UserDevicesService from "../../../services/UserDevicesService";
import { useSelector } from "react-redux";

const NewConfiguration: React.FC<{
  opened: any,
  close: any
}> = ({ opened, close }) => {

  const [patients, setPatients] = useState<any[]>([])
  const [fetchingPatients, setFetchingPatients] = useState<boolean>(false)

  const [fetchingFoodLists, setFetchingFoodLists] = useState<boolean>(false)
  const [foodLists, setFoodLists] = useState<any[]>([])

  const [tokens, setTokens] = useState<any[]>([])
  const [fetchingTokens, setFetchingTokens] = useState<boolean>(false)
  const { translations } = useSelector((state: any) => state.lang);
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const form = useForm({
    initialValues: {
      food_list_id: -1,
      patient_id: -1,
      room: '',
      user_device_id: -1,
      is_recording: false,
      status: 'queue',
      is_buffet: false
    },

    validate: {
      food_list_id: (value) => value <= 0 ? (translations.foodListID ?? "translations.foodListID") : null,
      patient_id: (value) => value <= 0 ? (translations.patientID ?? "translations.patientID") : null,
      user_device_id: (value) => value <= 0 ? (translations.deviceID ?? "translations.deviceID") : null,
      status: (value) => value !== 'draft' && value !== 'queue' ? (translations.statusStatus ?? "translations.statusStatus") : null,
    },
  });

  useEffect(() => {
    setFetchingPatients(true)
    AugmentedFoodService.listPatients()
        .then(setPatients)
        .catch(genericError)
        .finally(() => setFetchingPatients(false))

    setFetchingFoodLists(true)
    AugmentedFoodService.listFoodLists()
        .then(setFoodLists)
        .catch(genericError)
        .finally(() => setFetchingFoodLists(false))

    setFetchingTokens(true);
    UserDevicesService.listUserDevices()
        .then(setTokens)
        .catch(genericError)
        .finally(() => setFetchingTokens(false))
  }, [opened])

  useEffect(() => {
    if ( opened ) {
      form.reset();
    }
  }, [opened])

  const onSubmit = (values: any) => {
    const c = structuredClone(values);
    setIsSaving(true)
    if ( !c.is_recording ) {
      delete c.is_recording;
    }
    if ( !c.is_buffet ) {
      delete c.is_buffet;
    }
    AugmentedFoodService
        .createConfiguration(c)
        .then((r) => {
          close(r);
        })
        .catch(genericError)
        .finally(() => setIsSaving(false))
  }


  return (
      <>
        <Modal opened={opened}
               onClose={() => close(null)}
               title={translations.newConfig ?? "translations.newConfig"}
               closeOnClickOutside={false}
               zIndex={999}
               lockScroll={false}
               size={'100%'}>

          <form onSubmit={form.onSubmit(onSubmit)}>

            <Grid>
              <Grid.Col span={6}>
                <Stack>
                  <Select
                      required
                      withinPortal
                      icon={fetchingFoodLists ?
                          <FontAwesomeIcon icon={faSpinner} spin/> : null}
                      disabled={fetchingFoodLists}
                      zIndex={1001}
                      {...form.getInputProps('food_list_id')}
                      label={translations.foodList ?? "translations.foodList"}
                      searchable
                      clearable
                      placeholder={translations.searchByName ?? "translations.searchByName"}
                      data={foodLists.map(p => ({
                        value: p.id,
                        label: p.name
                      }))}
                  />

                  <Select
                      icon={fetchingPatients ?
                          <FontAwesomeIcon icon={faSpinner} spin/> : null}
                      disabled={fetchingPatients}
                      required
                      withinPortal
                      zIndex={1001}
                      {...form.getInputProps('patient_id')}
                      label={translations.selectPatient ?? "translations.selectPatient"}
                      searchable
                      clearable
                      placeholder={translations.searchPatient ?? "translations.searchPatient"}
                      data={patients.map(p => ({ value: p.id, label: p.code }))}
                  />
                </Stack>
              </Grid.Col>
              <Grid.Col span={6}>
                <Stack>
                  <Select
                      required
                      withinPortal
                      zIndex={1001}
                      {...form.getInputProps('room')}
                      label={translations.labelRoom ?? "translations.labelRoom"}
                      placeholder={translations.selectRoom ?? "translations.selectRoom"}
                      data={[
                        {
                          value: 'restaurant',
                          label: translations.ristorante ?? "translations.ristorante"
                        },
                        {
                          value: 'kitchen',
                          label: translations.cucina ?? "translations.cucina"
                        },
                        { value: 'Bedroom', label: translations.bedroom },
                        {
                          value: 'Dining_room',
                          label: translations.dining_room
                        },
                      ]}
                  />

                  <Select
                      required
                      withinPortal
                      zIndex={1001}
                      icon={fetchingTokens ?
                          <FontAwesomeIcon icon={faSpinner} spin/> : null}
                      disabled={fetchingTokens}
                      {...form.getInputProps('user_device_id')}
                      label={translations.selectDevice ?? "translations.selectDevice"}
                      searchable
                      clearable
                      placeholder={translations.pickOne ?? "translations.pickOne"}
                      data={tokens.filter(t => t.device_type === 'oculus').map((t: any) => ({
                        value: t.id,
                        label: t.device_name
                      }))}
                  />

                  <Checkbox label={translations.buffet ?? "translations.buffet"}
                            {...form.getInputProps('is_buffet', { type: "checkbox" })}
                  ></Checkbox>
                </Stack>
              </Grid.Col>
            </Grid>


            <Stack mt={24}>
              <Checkbox
                  label={translations.labelRecord ?? "translations.labelRecord"}
                  {...form.getInputProps('is_recording', { type: "checkbox" })}
              ></Checkbox>
              <Select
                  required
                  withinPortal
                  zIndex={1001}
                  {...form.getInputProps('status')}
                  label={translations.labelStatus ?? "translations.labelStatus"}
                  readOnly={true}
                  data={[
                    {
                      value: 'draft',
                      label: translations.bozza ?? "translations.bozza"
                    },
                    {
                      value: 'queue',
                      label: translations.coda ?? "translations.coda"
                    },
                  ]}
              />
              <Group position="right" mt="md">
                <Button type="submit"
                        loading={isSaving}
                        disabled={isSaving}
                >
                  {translations.buttonCreate ?? "translations.buttonCreate"}
                </Button>
              </Group>
            </Stack>
          </form>
        </Modal>
      </>
  );
}
export default NewConfiguration;